<script setup lang="ts">
import { computed } from 'vue'
import { useOrderCountdown } from '~/composables/useOrderCountdown'

const props = defineProps<{
  /** Order payment deadline used for the live countdown. */
  deadline?: string | null
}>()

const emit = defineEmits<{
  expire: []
}>()

const { remainingMs, label, isExpired, isUrgent } = useOrderCountdown(
  () => props.deadline,
  () => {
    emit('expire')
  }
)

const show = computed(() => label.value !== null && !isExpired.value)

const remainingMinutes = computed(() => {
  const ms = remainingMs.value
  if (ms === null || ms === 0) return null
  return Math.ceil(ms / 60_000)
})
</script>

<template>
  <div
    v-show="show"
    class="contents"
  >
    <!-- Critical (≤2 min): live countdown, red urgency -->
    <div
      v-if="isUrgent"
      class="bg-danger-50 dark:bg-danger-900/20 dark:ring-danger-400/20 dark:text-danger-400 font-medium gap-1 inline-flex items-center px-2.5 py-1 ring-1 ring-danger-600/20 ring-inset rounded-md text-danger-700 text-xs"
    >
      <svg
        class="shrink-0 size-3"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="2"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M12 6v6l4 2m6-2a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z"
        /></svg
      >{{ $t('commerce.orderCountdownBadge.respondInLabel', { label }) }}
    </div>

    <!-- Normal (>2 min): matches Pending status badge colors -->
    <div
      v-else
      class="bg-warning-50 dark:bg-warning-900/20 dark:ring-warning-400/20 dark:text-warning-400 font-medium gap-1 inline-flex items-center px-2.5 py-1 ring-1 ring-inset ring-warning-600/20 rounded-md text-warning-700 text-xs"
    >
      <svg
        class="shrink-0 size-3"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="2"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M12 6v6l4 2m6-2a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z"
        /></svg
      >{{
        $t('commerce.orderCountdownBadge.respondInMinutes', {
          count: remainingMinutes
        })
      }}
    </div>
  </div>
</template>
